import styles from './VirtualizedRowV8.module.scss';

import { Row } from '@tanstack/react-table';
import { VirtualItem, Virtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import React, { Fragment } from 'react';

import useLongPress from '../../hooks/useLongPress';
import { SELECT_BOX_MARKER } from '../../utils/makeRowsSelectable';
import { RowVirtualizerV8Props } from '../loader/RowVirtualizerV8';
import { TableSkeletonRowV8 } from '../table-skeleton-row/TableSkeletonRowV8';

import { RowV8 } from './components/row/RowV8';
import { SEPARATOR_ROW_HEIGHT, SeparatorRow } from './components/SeparatorRow';

interface VirtualizedRowProps
  extends Pick<
    RowVirtualizerV8Props,
    | 'classes'
    | 'cardsView'
    | 'cardConfig'
    | 'onRowClick'
    | 'onRowDoubleClick'
    | 'onRowContextMenu'
    | 'ignoreLongPress'
  > {
  height: number;
  row: Row<any>;
  virtualRow: VirtualItem;
  rowVirtualizer: Virtualizer<HTMLDivElement, HTMLDivElement>;
  isUnderPressSelect?: boolean;
  setUnderPressSelect?: (value: boolean) => void;
  allowAutoHeight?: boolean;
}

export function VirtualizedRow(props: VirtualizedRowProps) {
  const {
    row,
    virtualRow,
    rowVirtualizer,
    classes,
    height: heightProp,
    cardsView = false,
    cardConfig = null,
    onRowClick,
    onRowDoubleClick,
    onRowContextMenu,
    ignoreLongPress = false,
    isUnderPressSelect,
    setUnderPressSelect,
    allowAutoHeight = false,
  } = props;

  // Table's state is not actually used here, but it is passed as prop to the
  // memoized Row component to avoid rendering outdated view.
  // If rendering outside of context provider just use an empty object as it
  // will always force a re-render.
  const rowEventHandlers = {
    onContextMenu(event: React.MouseEvent<HTMLDivElement>) {
      const { nativeEvent } = event;

      if (
        nativeEvent instanceof PointerEvent &&
        nativeEvent.pointerType === 'touch'
      ) {
        event.preventDefault();
      } else {
        onRowContextMenu?.(event, row);
      }
    },
    onDoubleClick: handleRowDoubleClick,
    ...useLongPress({
      onClick(event) {
        onRowClick?.(event, row);
      },
      onLongPress() {
        if (ignoreLongPress) return;
        setUnderPressSelect?.(true);
        row.toggleSelected();
      },
    }),
  };

  const isCardsView = Boolean(cardsView && cardConfig);

  const style: React.CSSProperties = {
    position: 'absolute',
    transform: `translateY(${virtualRow.start}px)`,
    width: '100%',
  };

  if (!row || row.original.skeleton) {
    return <TableSkeletonRowV8 style={{ ...style, height: heightProp }} />;
  }

  function handleRowDoubleClick(event: React.MouseEvent<HTMLElement>) {
    if (!onRowDoubleClick) {
      return;
    }

    // Ignore double clicks on the checkbox element
    const target = event.target as HTMLElement;

    if (target.closest(`[data-${SELECT_BOX_MARKER}=true]`)) {
      return;
    }

    // Ignore double clicks if any of the modifier keys are pressed (Ctrl, Shift, Command)
    if (event.ctrlKey || event.shiftKey || event.metaKey) {
      return;
    }

    onRowDoubleClick(row.id);
  }

  const isSectionStart = row?.original.meta?.isSectionStart ?? false;
  if (!isSectionStart) {
    return renderRow();
  }

  return (
    <Fragment key={virtualRow?.key ?? row.id}>
      {renderSectionStartRow()}
      {renderRow()}
    </Fragment>
  );

  function renderSectionStartRow() {
    const sectionLabel = row.original.meta.sectionLabel ?? '';
    return <SeparatorRow label={sectionLabel} rowStyle={style} />;
  }

  function renderRow() {
    const height = isCardsView ? undefined : heightProp;
    return (
      <div
        {...rowEventHandlers}
        role="row"
        key={virtualRow?.key ?? row.id}
        data-index={virtualRow.index}
        data-section-start={isSectionStart || null}
        ref={rowVirtualizer.measureElement}
        className={clsx({ [styles.autoHeight]: allowAutoHeight })}
        style={{
          ...style,
          transform: virtualRow
            ? `translateY(${
                virtualRow.start + (isSectionStart ? SEPARATOR_ROW_HEIGHT : 0)
              }px)`
            : null,
          // If the row is the footer for a group, we disable the pointer-events like `onClick`.
          ...(row.original.meta?.isGroupFooter && { pointerEvents: 'none' }),
        }}
      >
        <RowV8
          row={row}
          classes={classes}
          cardsView={cardsView}
          cardConfig={cardConfig}
          isGroupFooter={row.original.meta?.isGroupFooter}
          isUnderPressSelect={isUnderPressSelect}
          setUnderPressSelect={setUnderPressSelect}
          style={{ height }}
        />
      </div>
    );
  }
}
